/* eslint-disable indent */
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { AnimatePresence } from "framer-motion";
import { Coins, Crown, Flame, Gem, Loader2, Mountain, Scroll, Skull, Sword } from "lucide-react";
import CommandSelect from "@/components/atoms/CommandSelect";
import FormField from "@/components/atoms/FormField";
import GenerationSkeleton from "@/components/atoms/GenerationSkeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { editLoot, generateLoot } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { AppReducerActionType, LootGenerationRequest, LootGenerationResponseDTO, LootGenerationResultDTO } from "@/types";
import LootView from "../LootView";
interface LootGeneratorFormProps {
  onGenerate?: (loot: LootGenerationResultDTO) => void;
}
const themes = [{
  value: "Dungeon",
  icon: Skull,
  description: "Classic dungeon treasures and artifacts"
}, {
  value: "Dragon's Hoard",
  icon: Flame,
  description: "Magnificent treasures from a dragon's collection"
}, {
  value: "Noble's Cache",
  icon: Crown,
  description: "Refined items and valuable artifacts from nobility"
}, {
  value: "Ancient Ruins",
  icon: Scroll,
  description: "Historical artifacts and forgotten treasures"
}, {
  value: "Mountain Keep",
  icon: Mountain,
  description: "Dwarven crafted items and precious gems"
}, {
  value: "Magical Treasury",
  icon: Gem,
  description: "Arcane items and magical artifacts"
}, {
  value: "Battlefield Spoils",
  icon: Sword,
  description: "War trophies and military equipment"
}, {
  value: "Other",
  icon: Coins,
  description: "Create your own custom loot theme"
}] as const;
const GAME_MODES = ["Default (D&D 5e)", "Pathfinder", "OSR", "Generic Fantasy"];
const LootGeneratorForm: React.FC<LootGeneratorFormProps> = ({
  onGenerate
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<LootGenerationResultDTO | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: {
      errors
    }
  } = useForm<LootGenerationRequest & {
    customTheme?: string;
    monsterName?: string;
    monsterCount?: number;
  }>({
    defaultValues: {
      partyLevel: 1,
      theme: "Dungeon",
      gameType: "Default (D&D 5e)",
      monstersFought: [],
      customTheme: ""
    }
  });
  const selectedTheme = watch("theme");
  const monsterName = watch("monsterName");
  const monsterCount = watch("monsterCount");
  const addMonster = () => {
    if (monsterName && monsterCount) {
      setValue("monstersFought", [...watch("monstersFought"), {
        monsterName,
        count: monsterCount
      }]);
      setValue("monsterName", "");
      setValue("monsterCount", undefined);
    }
  };
  const removeMonster = (index: number) => {
    const currentMonsters = watch("monstersFought");
    setValue("monstersFought", currentMonsters.filter((_, i) => i !== index));
  };
  const onSubmit = async (data: LootGenerationRequest & {
    customTheme?: string;
  }) => {
    setIsLoading(true);
    setErrorMessage(null);
    setResult(null);
    try {
      // Check if user is logged in
      if (!state.isLoggedIn) {
        dispatch({
          type: AppReducerActionType.SHOW_AUTH_MODAL,
          payload: {
            ...state,
            showAuthModal: true
          }
        });
        return;
      }

      // // Check if user has enough gold
      // if (
      //     state.userPremiumLevel === UserPremiumType.NONE &&
      //     state.gold < 1
      // ) {
      //     dispatch({
      //         type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
      //         payload: {
      //             ...state,
      //             showSubscriptionTable: true,
      //         },
      //     });
      //     return;
      // } else if (state.gold < 1) {
      //     dispatch({
      //         type: AppReducerActionType.SHOW_PRICING_TABLE,
      //         payload: {
      //             ...state,
      //             showPricingTable: true,
      //         },
      //     });
      //     return;
      // }

      // Use custom values if "Other" is selected
      const formData: LootGenerationRequest = {
        ...data,
        theme: data.theme === "Other" ? data.customTheme || "Custom Theme" : data.theme
      };
      const response = await generateLoot(formData);
      if (response.error) {
        setErrorMessage(response.error);
      } else if (response.response) {
        setResult(response.response);
        if (onGenerate) {
          onGenerate(response.response);
        }
      }
    } catch (error) {
      console.error("Error generating loot:", error);
      setErrorMessage("An error occurred while generating the loot.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleEdit = async (id: string, data: LootGenerationResponseDTO) => {
    try {
      const response = await editLoot(id, data);
      if (response.error) {
        console.error("Error editing loot:", response.error);
      } else if (response.response && result) {
        setResult({
          ...result,
          outputData: response.response
        });
      }
    } catch (error) {
      console.error("Error editing loot:", error);
    }
  };
  return <div className="w-full max-w-6xl mx-auto px-4" data-sentry-component="LootGeneratorForm" data-sentry-source-file="index.tsx">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 shadow-xl rounded-xl border border-slate-700 overflow-hidden">
                    <div className="p-6 border-b border-slate-700">
                        <h2 className="text-xl font-semibold text-slate-200">
                            Generate Loot
                        </h2>
                        <p className="text-sm text-slate-400 mt-1">
                            Fill in the details below to generate treasure and
                            rewards
                        </p>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                        <FormField label="Party Level" type="number" register={register("partyLevel", {
            required: "Party level is required",
            min: {
              value: 1,
              message: "Minimum level is 1"
            },
            max: {
              value: 20,
              message: "Maximum level is 20"
            }
          })} error={errors.partyLevel} placeholder="Enter party level (1-20)" data-sentry-element="FormField" data-sentry-source-file="index.tsx" />

                        <CommandSelect label="Theme" options={themes} value={selectedTheme} onChange={value => setValue("theme", value)} register={register} customFieldName="customTheme" error={errors.customTheme} placeholder="Select theme..." searchPlaceholder="Search themes..." emptyMessage="No theme found." customFieldPlaceholder="Enter your custom theme..." data-sentry-element="CommandSelect" data-sentry-source-file="index.tsx" />

                        <div>
                            <Label htmlFor="gameType" data-sentry-element="Label" data-sentry-source-file="index.tsx">Game System</Label>
                            <Select value={watch("gameType")} onValueChange={value => setValue("gameType", value)} data-sentry-element="Select" data-sentry-source-file="index.tsx">
                                <SelectTrigger id="gameType" className="!bg-slate-950 border-slate-700 text-slate-200" data-sentry-element="SelectTrigger" data-sentry-source-file="index.tsx">
                                    <SelectValue placeholder="Select game system" data-sentry-element="SelectValue" data-sentry-source-file="index.tsx" />
                                </SelectTrigger>
                                <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="index.tsx">
                                    {GAME_MODES.map(mode => <SelectItem key={mode} value={mode}>
                                            {mode}
                                        </SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-4">
                            <Label data-sentry-element="Label" data-sentry-source-file="index.tsx">Monsters Fought</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr,120px,auto] gap-4">
                                <FormField label="Monster Name" register={register("monsterName")} placeholder="Enter monster name" data-sentry-element="FormField" data-sentry-source-file="index.tsx" />
                                <FormField label="Count" type="number" register={register("monsterCount")} placeholder="How many?" data-sentry-element="FormField" data-sentry-source-file="index.tsx" />
                                <Button type="button" onClick={addMonster} className="self-end" variant="secondary" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                    Add Monster
                                </Button>
                            </div>
                            <div className="grid gap-2 max-h-[200px] overflow-y-auto">
                                {watch("monstersFought").map((monster, index) => <div key={index} className="flex items-center justify-between bg-slate-700/20 p-2 rounded-lg">
                                            <span className="text-slate-200">
                                                {monster.count}x{" "}
                                                {monster.monsterName}
                                            </span>
                                            <Button type="button" variant="destructive" size="sm" onClick={() => removeMonster(index)}>
                                                Remove
                                            </Button>
                                        </div>)}
                            </div>
                        </div>

                        <Button type="submit" disabled={isLoading} className="w-full py-4 px-6 !rounded-lg transition-all !bg-emerald-500 hover:!bg-emerald-600 !shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            {isLoading ? <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Generating Loot...
                                </> : <>
                                    <Coins className="mr-2 h-4 w-4" />
                                    Generate Loot
                                </>}
                        </Button>
                    </form>
                </div>

                <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="index.tsx">
                    {isLoading ? <GenerationSkeleton /> : errorMessage ? <div className="bg-red-900/20 text-red-400 rounded-xl p-6 border border-red-900">
                            {errorMessage}
                        </div> : result ? <div className="bg-slate-800/50 backdrop-blur-sm shadow-xl rounded-xl overflow-hidden border border-slate-700 p-6">
                            <LootView loot={result} onEdit={handleEdit} />
                        </div> : <div className="bg-slate-800/50 backdrop-blur-sm shadow-xl rounded-xl border border-slate-700 transition-all hover:border-sky-500/30">
                            <div className="p-8 text-center space-y-4">
                                <div className="w-16 h-16 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto">
                                    <Coins className="w-8 h-8 text-sky-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium text-slate-200">
                                        Your Generated Loot Will Appear Here
                                    </h3>
                                    <p className="text-sm text-slate-400 max-w-md mx-auto mt-2">
                                        Fill out the information on the left to
                                        generate appropriate loot for your
                                        party. The Party Level and monsters
                                        fought will affect the quality and
                                        quantity of the loot generated.
                                    </p>
                                </div>
                                <div className="h-[1px] bg-gradient-to-r from-slate-700/0 via-sky-500/30 to-slate-700/0" />
                            </div>
                        </div>}
                </AnimatePresence>
            </div>
        </div>;
};
export default LootGeneratorForm;